import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah, rupiahShort } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";
import { Banknote, ArrowLeftRight, QrCode, TrendingUp, Receipt, Wallet, AlertTriangle } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const today = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (!data) return (
    <>
      <PageHeader title="Dashboard" subtitle={today} />
      <div className="flex-1 flex items-center justify-center text-ui-muted">Memuat…</div>
    </>
  );

  const cards = [
    { label: "Pendapatan Hari Ini", value: rupiah(data.total_revenue), icon: TrendingUp, color: "text-brand-readable", bg: "bg-brand-soft" },
    { label: "Transaksi", value: data.tx_count, icon: Receipt, color: "text-accent-readable", bg: "bg-accent-soft" },
    { label: "Rata-rata / Transaksi", value: rupiah(data.avg_transaction), icon: Wallet, color: "text-accent-readable", bg: "bg-accent-soft" },
    { label: "Total Kembalian", value: rupiah(data.total_change), icon: Banknote, color: "text-warning-readable", bg: "bg-warning-soft" },
  ];

  const methods = [
    { key: "tunai", label: "Tunai", icon: Banknote, color: "var(--masivi-primary)" },
    { key: "transfer", label: "Transfer", icon: ArrowLeftRight, color: "var(--masivi-secondary)" },
    { key: "qris", label: "QRIS", icon: QrCode, color: "var(--masivi-accent)" },
  ];
  const methodTotal = methods.reduce((s, m) => s + (data.method_breakdown[m.key] || 0), 0) || 1;

  return (
    <>
      <PageHeader title="Dashboard" subtitle={today} />
      <div className="flex-1 overflow-y-auto p-5" data-testid="sales-dashboard">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {cards.map((c) => (
            <div key={c.label} className="rounded-xl border border-ui-line bg-ui-soft p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-ui-muted uppercase tracking-wide">{c.label}</span>
                <div className={`h-9 w-9 rounded-lg ${c.bg} flex items-center justify-center`}>
                  <c.icon className={`h-5 w-5 ${c.color}`} />
                </div>
              </div>
              <div className="font-mono font-extrabold text-2xl mt-2">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 rounded-xl border border-ui-line bg-ui-soft p-4">
            <h3 className="font-heading font-semibold mb-4">Tren Penjualan 7 Hari</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--masivi-border)" />
                <XAxis dataKey="date" stroke="var(--masivi-card-muted)" fontSize={12} />
                <YAxis stroke="var(--masivi-card-muted)" fontSize={12} tickFormatter={rupiahShort} width={60} />
                <Tooltip contentStyle={{ background: "var(--masivi-card-bg)", color: "var(--masivi-card-text)", border: "1px solid var(--masivi-border)", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
                <Line type="monotone" dataKey="total" stroke="var(--masivi-primary)" strokeWidth={3} dot={{ fill: "var(--masivi-primary)" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-xl border border-ui-line bg-ui-soft p-4">
            <h3 className="font-heading font-semibold mb-4">Metode Pembayaran</h3>
            <div className="space-y-4">
              {methods.map((m) => {
                const val = data.method_breakdown[m.key] || 0;
                const pct = Math.round((val / methodTotal) * 100);
                return (
                  <div key={m.key}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2"><m.icon className="h-4 w-4" style={{ color: m.color }} /> {m.label}</span>
                      <span className="font-mono">{rupiah(val)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-ui-muted-surface overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: m.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-ui-line bg-ui-soft p-4">
            <h3 className="font-heading font-semibold mb-4">Produk Terlaris Hari Ini</h3>
            {data.top_products.length === 0 ? (
              <p className="text-ui-muted text-sm py-8 text-center">Belum ada penjualan hari ini</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.top_products} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" stroke="var(--masivi-card-muted)" fontSize={12} hide />
                  <YAxis type="category" dataKey="name" stroke="var(--masivi-card-muted)" fontSize={11} width={110} />
                  <Tooltip contentStyle={{ background: "var(--masivi-card-bg)", color: "var(--masivi-card-text)", border: "1px solid var(--masivi-border)", borderRadius: 12 }} formatter={(v, n) => n === "qty" ? [v + " pcs", "Terjual"] : rupiah(v)} />
                  <Bar dataKey="qty" fill="var(--masivi-accent)" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-xl border border-ui-line bg-ui-soft p-4">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warning-readable" /> Stok Menipis</h3>
            {data.low_stock.length === 0 ? (
              <p className="text-ui-muted text-sm py-8 text-center">Semua stok aman 👍</p>
            ) : (
              <div className="space-y-2 max-h-[260px] overflow-y-auto">
                {data.low_stock.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-ui-soft/70 border border-ui-line px-3 py-2">
                    <span className="text-sm">{p.name}</span>
                    <span className="font-mono text-warning-readable text-sm">sisa {p.stock}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
